import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  collection,
  onSnapshot,
  doc,
  setDoc,
  deleteDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase/firebase";

// CSS
import "./HoroscopoSemanal.css";

const SIGNOS = [
  { id: "aries", nome: "Áries", icone: "♈", elemento: "Fogo" },
  { id: "touro", nome: "Touro", icone: "♉", elemento: "Terra" },
  { id: "gemeos", nome: "Gêmeos", icone: "♊", elemento: "Ar" },
  { id: "cancer", nome: "Câncer", icone: "♋", elemento: "Água" },
  { id: "leao", nome: "Leão", icone: "♌", elemento: "Fogo" },
  { id: "virgem", nome: "Virgem", icone: "♍", elemento: "Terra" },
  { id: "libra", nome: "Libra", icone: "♎", elemento: "Ar" },
  { id: "escorpiao", nome: "Escorpião", icone: "♏", elemento: "Água" },
  { id: "sagitario", nome: "Sagitário", icone: "♐", elemento: "Fogo" },
  { id: "capricornio", nome: "Capricórnio", icone: "♑", elemento: "Terra" },
  { id: "aquario", nome: "Aquário", icone: "♒", elemento: "Ar" },
  { id: "peixes", nome: "Peixes", icone: "♓", elemento: "Água" },
];

function formatDate(ts) {
  try {
    if (!ts) return "";
    if (typeof ts?.toDate === "function") {
      return ts.toDate().toLocaleString("pt-BR");
    }
    if (ts instanceof Date) return ts.toLocaleString("pt-BR");
    return "";
  } catch {
    return "";
  }
}

export default function HoroscopoSemanal() {
  const topRef = useRef(null);
  const [lista, setLista] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSignId, setSelectedSignId] = useState("aries");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ type: "", text: "" });

  const [form, setForm] = useState({
    nome: "Áries",
    icone: "♈",
    elemento: "Fogo",
    periodo: "",
    energiaSemana: "",
    melhorDia: "",
    numerosSorte: "",
    tendencias: "",
    atencao: "",
    conselho: "",
    emoji: "",
  });

  const mapById = useMemo(() => {
    const m = new Map();
    lista.forEach((item) => m.set(item.id, item));
    return m;
  }, [lista]);

  const signoAtual = useMemo(() => {
    return SIGNOS.find((s) => s.id === selectedSignId) || SIGNOS[0];
  }, [selectedSignId]);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "horoscopo_semanal"), (snap) => {
      const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const ordered = SIGNOS.map((s) => arr.find((x) => x.id === s.id)).filter(Boolean);
      const rest = arr.filter((x) => !SIGNOS.some((s) => s.id === x.id));
      setLista([...ordered, ...rest]);
      setLoading(false);
    });

    return () => unsub();
  }, []);

  useEffect(() => {
    const base = {
      nome: signoAtual.nome,
      icone: signoAtual.icone,
      elemento: signoAtual.elemento,
      periodo: "",
      energiaSemana: "",
      melhorDia: "",
      numerosSorte: "",
      tendencias: "",
      atencao: "",
      conselho: "",
      emoji: "",
    };

    const existente = mapById.get(selectedSignId);
    if (existente) {
      setForm({
        nome: existente.nome ?? base.nome,
        icone: existente.icone ?? base.icone,
        elemento: existente.elemento ?? base.elemento,
        periodo: existente.periodo ?? "",
        energiaSemana: existente.energiaSemana ?? "",
        melhorDia: existente.melhorDia ?? "",
        numerosSorte: existente.numerosSorte ?? "",
        tendencias: existente.tendencias ?? "",
        atencao: existente.atencao ?? "",
        conselho: existente.conselho ?? "",
        emoji: existente.emoji ?? "",
      });
    } else {
      setForm(base);
    }

    setMsg({ type: "", text: "" });
  }, [selectedSignId, signoAtual, mapById]);

  function setField(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function validate() {
    if (!selectedSignId) return "Selecione um signo.";
    if (!form.periodo?.trim()) return "Preencha o período do signo.";
    if (!form.energiaSemana?.trim()) return "Preencha a energia da semana.";
    if (!form.melhorDia?.trim()) return "Preencha o melhor dia.";
    if (!form.conselho?.trim()) return "Preencha o conselho amoroso.";
    return "";
  }

  async function handleSave(e) {
    e.preventDefault();
    setMsg({ type: "", text: "" });

    const err = validate();
    if (err) {
      setMsg({ type: "error", text: err });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        nome: signoAtual.nome,
        icone: signoAtual.icone,
        elemento: signoAtual.elemento,
        periodo: form.periodo.trim(),
        energiaSemana: form.energiaSemana.trim(),
        melhorDia: form.melhorDia.trim(),
        numerosSorte: form.numerosSorte.trim(),
        tendencias: form.tendencias.trim(),
        atencao: form.atencao.trim(),
        conselho: form.conselho.trim(),
        emoji: form.emoji.trim(),
        updatedAt: serverTimestamp(),
      };

      await setDoc(doc(db, "horoscopo_semanal", selectedSignId), payload, {
        merge: true,
      });

      setMsg({ type: "success", text: "Horóscopo salvo com sucesso ✅" });
    } catch (error) {
      console.error(error);
      setMsg({
        type: "error",
        text: "Erro ao salvar. Verifique permissões do Firestore.",
      });
    } finally {
      setSaving(false);
    }
  }

  function handleEdit(id) {
    setSelectedSignId(id);
    setTimeout(() => {
      topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  }

  async function handleDelete(id) {
    const s = SIGNOS.find((x) => x.id === id);
    const ok = window.confirm(
      `Tem certeza que deseja excluir o horóscopo semanal de ${s?.nome || id}?`
    );
    if (!ok) return;

    try {
      await deleteDoc(doc(db, "horoscopo_semanal", id));
      setMsg({ type: "success", text: "Registro excluído ✅" });
    } catch (error) {
      console.error(error);
      setMsg({
        type: "error",
        text: "Erro ao excluir. Verifique permissões do Firestore.",
      });
    }
  }

  return (
    <div className="horoscopo-page">
      <div className="hs-container" ref={topRef}>
        <div className="hs-header">
          <div className="hs-title">
            <h1>Horóscopo do Amor — Semanal</h1>
            <p>
              Cadastre/atualize as informações semanais por signo (Firestore:
              <b> horoscopo_semanal</b>).
            </p>
          </div>

          <div className="hs-chip">
            <span className="hs-chip-icon">{signoAtual.icone}</span>
            <div className="hs-chip-text">
              <strong>{signoAtual.nome}</strong>
              <small>{signoAtual.elemento}</small>
            </div>
          </div>
        </div>

        {msg.text && (
          <div className={`hs-alert ${msg.type}`}>
            <span>{msg.text}</span>
          </div>
        )}

        <form className="hs-card hs-form" onSubmit={handleSave}>
          <div className="hs-form-row">
            <div className="hs-field">
              <label>Signo</label>
              <select
                value={selectedSignId}
                onChange={(e) => setSelectedSignId(e.target.value)}
              >
                {SIGNOS.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.icone} {s.nome} — {s.elemento}
                  </option>
                ))}
              </select>
            </div>

            <div className="hs-field">
              <label>Período (ex: 21/03 — 19/04)</label>
              <input
                type="text"
                value={form.periodo}
                onChange={(e) => setField("periodo", e.target.value)}
                placeholder="21/03 — 19/04"
              />
            </div>
          </div>

          <div className="hs-form-row">
            <div className="hs-field">
              <label>Energia da Semana (ex: Alta 🔥)</label>
              <input
                type="text"
                value={form.energiaSemana}
                onChange={(e) => setField("energiaSemana", e.target.value)}
                placeholder="Alta 🔥"
              />
            </div>

            <div className="hs-field">
              <label>Melhor dia para conexões</label>
              <input
                type="text"
                value={form.melhorDia}
                onChange={(e) => setField("melhorDia", e.target.value)}
                placeholder="Quinta-feira"
              />
            </div>
          </div>

          <div className="hs-form-row">
            <div className="hs-field">
              <label>Números da Sorte (ex: 03 • 11 • 19 • 27 • 44)</label>
              <input
                type="text"
                value={form.numerosSorte}
                onChange={(e) => setField("numerosSorte", e.target.value)}
                placeholder="03 • 11 • 19 • 27 • 44"
              />
            </div>

            <div className="hs-field">
              <label>Emoji final (ex: 💗✨)</label>
              <input
                type="text"
                value={form.emoji}
                onChange={(e) => setField("emoji", e.target.value)}
                placeholder="💗✨"
              />
            </div>
          </div>

          <div className="hs-form-row">
            <div className="hs-field">
              <label>Tendências Amorosas</label>
              <textarea
                rows={3}
                value={form.tendencias}
                onChange={(e) => setField("tendencias", e.target.value)}
                placeholder="Descreva as tendências amorosas da semana..."
              />
            </div>

            <div className="hs-field">
              <label>Ponto de Atenção</label>
              <textarea
                rows={3}
                value={form.atencao}
                onChange={(e) => setField("atencao", e.target.value)}
                placeholder="O que evitar / atenção da semana..."
              />
            </div>
          </div>

          <div className="hs-form-row">
            <div className="hs-field hs-field-full">
              <label>Conselho Amoroso</label>
              <textarea
                rows={3}
                value={form.conselho}
                onChange={(e) => setField("conselho", e.target.value)}
                placeholder="Conselho principal da semana..."
              />
            </div>
          </div>

          <div className="hs-actions">
            <button className="hs-btn hs-btn-primary" type="submit" disabled={saving}>
              {saving ? "Salvando..." : "Salvar / Atualizar"}
            </button>

            <button
              className="hs-btn hs-btn-ghost"
              type="button"
              onClick={() => {
                const base = SIGNOS.find((s) => s.id === selectedSignId) || SIGNOS[0];
                setForm({
                  nome: base.nome,
                  icone: base.icone,
                  elemento: base.elemento,
                  periodo: "",
                  energiaSemana: "",
                  melhorDia: "",
                  numerosSorte: "",
                  tendencias: "",
                  atencao: "",
                  conselho: "",
                  emoji: "",
                });
                setMsg({ type: "", text: "" });
              }}
            >
              Limpar campos
            </button>
          </div>
        </form>

        <div className="hs-list-header">
          <h2>Signos cadastrados</h2>
          <p>
            Abaixo aparecem os registros existentes em <b>horoscopo_semanal</b>.
          </p>
        </div>

        {loading ? (
          <div className="hs-empty">Carregando...</div>
        ) : (
          <div className="hs-grid">
            {SIGNOS.map((s) => {
              const item = mapById.get(s.id);
              const has = !!item;

              return (
                <div key={s.id} className={`hs-card hs-item ${has ? "" : "missing"}`}>
                  <div className="hs-item-top">
                    <div className="hs-item-sign">
                      <span className="hs-item-icon">{s.icone}</span>
                      <div>
                        <strong>{s.nome}</strong>
                        <small>{s.elemento}</small>
                      </div>
                    </div>

                    <div className="hs-item-actions">
                      <button
                        className="hs-mini hs-mini-edit"
                        onClick={() => handleEdit(s.id)}
                        type="button"
                      >
                        Editar
                      </button>
                      <button
                        className="hs-mini hs-mini-del"
                        onClick={() => handleDelete(s.id)}
                        type="button"
                        disabled={!has}
                        title={!has ? "Nada para excluir" : "Excluir registro"}
                      >
                        Excluir
                      </button>
                    </div>
                  </div>

                  {!has ? (
                    <div className="hs-item-body">
                      <p className="hs-muted">
                        Ainda não cadastrado. Clique em <b>Editar</b> para preencher.
                      </p>
                    </div>
                  ) : (
                    <div className="hs-item-body">
                      <div className="hs-kv">
                        <span>Período</span>
                        <b>{item.periodo || "-"}</b>
                      </div>
                      <div className="hs-kv">
                        <span>Energia</span>
                        <b>{item.energiaSemana || "-"}</b>
                      </div>
                      <div className="hs-kv">
                        <span>Melhor dia</span>
                        <b>{item.melhorDia || "-"}</b>
                      </div>

                      <div className="hs-divider" />

                      <div className="hs-block">
                        <span className="hs-block-title">Conselho</span>
                        <p className="hs-block-text">{item.conselho || "-"}</p>
                      </div>

                      <div className="hs-meta">
                        <span className="hs-meta-emoji">{item.emoji || "💗✨"}</span>
                        <span className="hs-meta-date">
                          Atualizado: {formatDate(item.updatedAt) || "—"}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="hs-footer-note">
          <p>
            Dica: no app Flutter, basta buscar o doc do signo em{" "}
            <b>horoscopo_semanal/{selectedSignId}</b> e renderizar os campos.
          </p>
        </div>
      </div>
    </div>
  );
}